import { queryCache, useMutation, useQuery, QueryConfig } from "react-query";

import {
  ApplicationCosign,
  CreateCosignApplicationRequest,
  DownloadApprovalLetterRequest,
  CosignInvitationEligibility,
} from "manager/interfaces/api";
import api from "manager/lib/api";
import cosignIntegrationApi from "manager/lib/cosignIntegrationApi";
import { NOTIFICATIONS } from "shared/config/constants";
import { openNotification } from "shared/utils/ui";

import { INTEGRATION_QUERY_KEYS } from "./integrationQueries";

const COSIGN_APPLICATIONS = "COSIGN_APPLICATIONS";
export const COSIGN_INVITATION_ELIGIBILITY = "COSIGN_INVITATION_ELIGIBILITY";

interface DisconnectCosignPropertyMappingRequest {
  cosignPropertyId: number | string;
}

interface UpdateCosignPropertyIsActiveRequest {
  cosignPropertyId: number | string;
  isActive: boolean;
}

interface UseCosignPropertyMutationOptions {
  onSuccess?: () => void;
}

const extractCosignApiErrorMessage = (error: any, fallback: string) =>
  error?.errors?.error ||
  error?.errors?.nonFieldErrors?.[0] ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

/**
 * Hook to fetch cosign applications for a specific application
 * @param applicationId - The application ID to fetch cosign data for
 * @param options - Additional react-query options
 */
export const useCosignApplications = (
  applicationId: string | number | null | undefined,
  options?: QueryConfig<ApplicationCosign, Error>,
  showNotification: boolean = true
) => {
  // Merge enabled logic: require applicationId AND respect custom enabled condition
  // Extract enabled from options to avoid it being overridden when spreading
  const customEnabled = options?.enabled;
  const enabled = !!applicationId && (customEnabled !== undefined ? customEnabled : true);
  
  const { data, isLoading, error, refetch } = useQuery<ApplicationCosign, Error>(
    [COSIGN_APPLICATIONS, applicationId ? applicationId: null],
    () => api.getCosignApplications({ applicationId: applicationId! }),
    {
      ...options,
      // Override enabled after spreading options to ensure our merged logic takes precedence
      enabled,
      onError: () =>
        showNotification !== false && openNotification(
          "Failed to load cosign application details",
          NOTIFICATIONS.error
        ),
    }
  );

  return {
    cosignApplication: data,
    isCosignApplicationLoading: isLoading,
    cosignApplicationError: error,
    refetchCosignApplication: refetch,
  };
};

/**
 * Hook to create a new cosign application
 */
export const useCreateCosignApplication = () => {
  const [
    createCosignApplication,
    { isLoading: isCreatingCosignApplication, error },
  ] = useMutation<
    ApplicationCosign,
    Error,
    CreateCosignApplicationRequest
  >(
    (params: CreateCosignApplicationRequest) =>
      api.createCosignApplication(params),
    {
      onSuccess: (data, variables) => {
        queryCache.invalidateQueries([
          COSIGN_APPLICATIONS,
          variables.applicationId,
        ]);
        openNotification(
          "We've sent the applicant an invitation to apply through Cosign. They'll receive an email from Cosign to complete their application and payment. This deal will update automatically once Cosign makes a decision.",
          NOTIFICATIONS.info,
          {message: "Cosign application initiated"}
        );
      },
      onError: (error: any) => {
        
        // Extract error message from API response
        // API returns: { error: "...", error_type: "..." }
        // After ApiError conversion, it becomes error.errors.error (camelCase)
        const errorMessage = 
          error?.errors?.error || 
          error?.errors?.nonFieldErrors?.[0] ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to create cosign application";
        
        openNotification(
          errorMessage,
          NOTIFICATIONS.error,
          {message: "Failed to initiate cosign application"}
        );
      },
    }
  );

  return {
    createCosignApplication,
    isCreatingCosignApplication,
    createCosignApplicationError: error,
  };
};

/**
 * Hook to download the approval letter for a cosign application
 */
export const useDownloadApprovalLetter = () => {
  const [
    downloadApprovalLetter,
    { isLoading: isDownloadingApprovalLetter, error },
  ] = useMutation<
    Blob,
    Error,
    DownloadApprovalLetterRequest
  >(
    (params: DownloadApprovalLetterRequest) =>
      api.downloadApprovalLetter(params),
    {
      onSuccess: () => {
        openNotification(
          "Approval letter downloaded successfully!",
          NOTIFICATIONS.info
        );
      },
      onError: (error: Error) => {
        openNotification(
          error.message || "Failed to download approval letter",
          NOTIFICATIONS.error
        );
      },
    }
  );

  return {
    downloadApprovalLetter,
    isDownloadingApprovalLetter,
    downloadApprovalLetterError: error,
  };
};


/**
 * Hook to cancel a cosign application
 */
export const useCancelCosignApplication = () => {
  const [
    cancelCosignApplication,
    { isLoading: isCancellingCosignApplication, error },
  ] = useMutation<
    void,
    Error,
    string
  >(
    (cosignUuid: string) =>
      api.cancelCosignApplication(cosignUuid),
    {
      onSuccess: () => {
        openNotification(
          "Cosign application canceled successfully!",
          NOTIFICATIONS.info
        );
      },
      onError: (error: Error) => {
        openNotification(
          error.message || "Failed to cancel cosign application",
          NOTIFICATIONS.error
        );
      },
    }
  );

  return {
    cancelCosignApplication,
    isCancellingCosignApplication,
    cancelCosignApplicationError: error,
  };
};

/**
 * Hook to check if a Cosign application invitation can be initiated for a specific application
 * @param applicationId - The application ID to check eligibility for
 * @param options - Additional react-query options
 */
export const useCosignApplicationInvitation = (
  applicationId: string | number | null | undefined,
  options?: QueryConfig<CosignInvitationEligibility, Error>,
  showNotification: boolean = false
) => {
  const customEnabled = options?.enabled;
  const enabled = !!applicationId && (customEnabled !== undefined ? customEnabled : true);
  const { data, isLoading, error, refetch } = useQuery<CosignInvitationEligibility, Error>(
    [COSIGN_INVITATION_ELIGIBILITY, applicationId ? applicationId: null],
    () => api.getCanInitiateCosign({ applicationId: applicationId! }),
    {

      ...options,
      enabled: enabled,
      onError: () =>
        showNotification &&
        openNotification(
          "Failed to check cosign invitation eligibility",
          NOTIFICATIONS.error
        ),
    }
  );

  return {
    cosignInvitationEligibility: data,
    isCosignInvitationEligibilityLoading: isLoading,
    cosignInvitationEligibilityError: error,
    refetchCosignInvitationEligibility: refetch,
  };
};

export const useDisconnectCosignPropertyMapping = (
  options: UseCosignPropertyMutationOptions = {}
) => {
  const [
    disconnectCosignPropertyMapping,
    { isLoading: isDisconnectingCosignPropertyMapping, error },
  ] = useMutation<
    { success: boolean },
    Error,
    DisconnectCosignPropertyMappingRequest
  >(
    ({ cosignPropertyId }) =>
      cosignIntegrationApi.deleteCosignPropertyMapping(cosignPropertyId),
    {
      onSuccess: async () => {
        await queryCache.invalidateQueries(
          INTEGRATION_QUERY_KEYS.getCosignPropertyMappings()
        );
        options.onSuccess?.();
        openNotification(
          "Cosign property mapping disconnected successfully.",
          NOTIFICATIONS.info
        );
      },
      onError: (error: any) => {
        openNotification(
          extractCosignApiErrorMessage(
            error,
            "Failed to disconnect Cosign property mapping."
          ),
          NOTIFICATIONS.error
        );
      },
    }
  );

  return {
    disconnectCosignPropertyMapping,
    isDisconnectingCosignPropertyMapping,
    disconnectCosignPropertyMappingError: error,
  };
};

export const useUpdateCosignPropertyIsActive = (
  options: UseCosignPropertyMutationOptions = {}
) => {
  const [
    updateCosignPropertyIsActive,
    { isLoading: isUpdatingCosignPropertyIsActive, error },
  ] = useMutation<
    void,
    Error,
    UpdateCosignPropertyIsActiveRequest
  >(
    ({ cosignPropertyId, isActive }) =>
      cosignIntegrationApi.updateCosignPropertyIsActive(
        cosignPropertyId,
        isActive
      ),
    {
      onSuccess: async (_data, variables) => {
        await queryCache.invalidateQueries(
          INTEGRATION_QUERY_KEYS.getCosignPropertyMappings()
        );
        options.onSuccess?.();
        openNotification(
          variables.isActive
            ? "Cosign enabled for this property."
            : "Cosign disabled for this property.",
          NOTIFICATIONS.info
        );
      },
      onError: (error: any) => {
        openNotification(
          extractCosignApiErrorMessage(
            error,
            "Failed to update Cosign property status."
          ),
          NOTIFICATIONS.error
        );
      },
    }
  );

  return {
    updateCosignPropertyIsActive,
    isUpdatingCosignPropertyIsActive,
    updateCosignPropertyIsActiveError: error,
  };
};
